import React from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';

interface MotionLiquidButtonProps {
    children: React.ReactNode;
    // eslint-disable-next-line react/require-default-props
    onClick?: () => void;
    // eslint-disable-next-line react/require-default-props
    disabled?: boolean;
    // eslint-disable-next-line react/require-default-props
    variant?: 'default' | 'bounce' | 'ripple' | 'blast' | 'glow' | 'shake';
}

const StyledMotionButton = styled(motion.button)`
    padding: 12px 24px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 25px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
    transition: background 0.3s ease, box-shadow 0.3s ease;

    &:hover {
        background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

// Animation variants for different button effects
const buttonVariants = {
    default: {
        animate: {
            scale: [1, 1.02, 1],
            transition: { duration: 0.3 },
        },
    },
    bounce: {
        animate: {
            y: [0, -10, 0, -5, 0],
            scale: [1, 1.02, 1],
            transition: { duration: 0.4 },
        },
    },
    ripple: {
        animate: {
            scale: [1, 1.15, 1.05, 1.1, 1],
            transition: { duration: 0.5 },
        },
    },
    blast: {
        animate: {
            scale: [1, 1.3, 0.8, 1.2, 1],
            rotate: [0, 10, -10, 5, 0],
            transition: { duration: 0.6 },
        },
    },
    glow: {
        animate: {
            scale: [1, 1.1, 1],
            boxShadow: [
                '0 4px 15px rgba(102, 126, 234, 0.3)',
                '0 0 30px rgba(250, 112, 154, 0.8)',
                '0 4px 15px rgba(102, 126, 234, 0.3)',
            ],
            transition: { duration: 0.4 },
        },
    },
    shake: {
        animate: {
            x: [0, -15, 15, -10, 10, -5, 5, 0],
            transition: { duration: 0.5 },
        },
    },
};

const MotionLiquidButton: React.FC<MotionLiquidButtonProps> = ({
    children,
    onClick,
    disabled = false,
    variant = 'default',
}) => {
    const [isAnimating, setIsAnimating] = React.useState(false);

    const handleClick = () => {
        if (disabled || isAnimating) return;

        setIsAnimating(true);

        // Call the onClick handler
        if (onClick) {
            onClick();
        }

        // Reset animation state after animation completes
        setTimeout(() => {
            setIsAnimating(false);
        }, buttonVariants[variant].animate.transition.duration * 1000);
    };

    const currentVariant = buttonVariants[variant];

    return (
        <StyledMotionButton
            animate={isAnimating ? currentVariant.animate : {}}
            onClick={handleClick}
            disabled={disabled}
            transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20,
            }}
        >
            {children}
        </StyledMotionButton>
    );
};

export default MotionLiquidButton;
